// Command palette (Ctrl+Shift+P): fuzzy-ish search over every editor
// command in the registry — tools, panels, page ops, exports. Runs the
// picked command and closes. Unavailable commands (when() false) and
// internals (inPalette: false) are hidden.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  shortcutLabel,
  type EditorCommand,
} from "../lib/commands.ts";

export function CommandPalette({
  commands,
  onClose,
}: {
  commands: readonly EditorCommand[];
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const matches = useMemo(() => {
    const available = commands.filter(
      (c) => c.inPalette !== false && (!c.when || c.when(null))
    );
    const q = query.trim().toLowerCase();
    if (!q) return available;
    const starts: EditorCommand[] = [];
    const contains: EditorCommand[] = [];
    for (const c of available) {
      const label = c.label.toLowerCase();
      if (label.startsWith(q)) starts.push(c);
      else if (label.includes(q) || c.id.toLowerCase().includes(q)) contains.push(c);
    }
    return [...starts, ...contains];
  }, [commands, query]);

  const clamped = Math.min(active, Math.max(0, matches.length - 1));

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${clamped}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [clamped, matches]);

  const run = (c: EditorCommand | undefined) => {
    if (!c) return;
    onClose();
    c.run();
  };

  return (
    <div
      className="absolute inset-0 z-50 flex items-start justify-center bg-black/30 pt-24"
      onPointerDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="w-full max-w-lg overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] shadow-xl"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-[color:var(--color-border)] px-3">
          <i className="ri-command-line text-[color:var(--color-fg-muted)]" aria-hidden />
          <input
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Escape") onClose();
              else if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, matches.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                run(matches[clamped]);
              }
            }}
            placeholder="Type a command…"
            aria-label="Search commands"
            className="h-11 min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
          <span className="text-[10px] text-[color:var(--color-fg-muted)]">Esc</span>
        </div>
        <div ref={listRef} role="listbox" aria-label="Commands" className="max-h-80 overflow-y-auto p-1">
          {matches.length === 0 && (
            <p className="px-3 py-4 text-center text-xs text-[color:var(--color-fg-muted)]">
              No matching command.
            </p>
          )}
          {matches.map((c, i) => (
            <button
              key={c.id}
              type="button"
              role="option"
              aria-selected={i === clamped}
              data-index={i}
              onPointerEnter={() => setActive(i)}
              onClick={() => run(c)}
              className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm ${
                i === clamped
                  ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                  : "hover:bg-[color:var(--color-bg-subtle)]"
              }`}
            >
              <i className={`${c.icon ?? "ri-flashlight-line"} shrink-0`} aria-hidden />
              <span className="min-w-0 flex-1 truncate">{c.label}</span>
              {c.shortcut && (
                <span
                  className={`shrink-0 text-xs ${
                    i === clamped ? "opacity-80" : "text-[color:var(--color-fg-muted)]"
                  }`}
                >
                  {shortcutLabel(c.shortcut)}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
